import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Clock, Calendar, Car, CreditCard, UserCheck, AlertCircle, Trash2 } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';
import Card, { CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useNotification } from '../context/NotificationContext';
import { formatName } from '../lib/utils';

const getNotificationIcon = (type) => {
  switch (type) {
    case 'booking_created':
    case 'booking_confirmed':
      return <Calendar className="h-5 w-5 text-blue-600" />;
    case 'booking_cancelled':
    case 'booking_rejected':
      return <AlertCircle className="h-5 w-5 text-red-600" />;
    case 'booking_completed':
      return <CheckCheck className="h-5 w-5 text-green-600" />;
    case 'payment':
      return <CreditCard className="h-5 w-5 text-purple-600" />;
    case 'driver_assigned':
      return <UserCheck className="h-5 w-5 text-indigo-600" />;
    case 'vehicle':
      return <Car className="h-5 w-5 text-orange-600" />;
    default:
      return <Bell className="h-5 w-5 text-gray-600" />;
  }
};

const getNotificationBg = (type, isRead) => {
  if (isRead) return 'bg-white';
  switch (type) {
    case 'booking_created':
    case 'booking_confirmed':
      return 'bg-blue-50';
    case 'booking_cancelled':
    case 'booking_rejected':
      return 'bg-red-50';
    case 'booking_completed':
      return 'bg-green-50';
    case 'payment':
      return 'bg-purple-50';
    default:
      return 'bg-gray-50';
  }
};

const formatTimeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Notifications = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead, fetchNotifications } = useNotification();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification._id);
    }
    // Navigate to action URL if available
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    } else if (notification.referenceId && notification.referenceModel === 'Booking') {
      navigate(`/bookings`);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        description={`You have ${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`}
        action={
          unreadCount > 0 ? (
            <Button variant="outline" onClick={markAllAsRead}>
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark all as read
            </Button>
          ) : null
        }
      />

      <Card>
        <CardContent className="p-0">
          {notifications.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`flex items-start gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors ${getNotificationBg(notification.type, notification.isRead)}`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${notification.isRead ? 'bg-gray-100' : 'bg-white shadow-sm'}`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${notification.isRead ? 'text-gray-700' : 'text-gray-900 font-medium'}`}>
                      {notification.title}
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">{notification.message}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Clock className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-400">{formatTimeAgo(notification.createdAt)}</span>
                    </div>
                  </div>
                  {!notification.isRead && (
                    <div className="flex-shrink-0 mt-2">
                      <div className="w-2.5 h-2.5 bg-blue-600 rounded-full"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Bell className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No notifications</h3>
              <p className="text-sm text-gray-500">You're all caught up! Notifications will appear here.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Notifications;
